/**
 * Colour arithmetic for the diagrams: tints of the palette, and the colour a stack of
 * translucent fills makes, so text on an overlap can pick the ink that reads.
 *
 * Exported images carry literal hex colours (see `palette.ts`), so a tint is computed
 * here rather than left to CSS opacity — except where an overlap should visibly darken,
 * as in a Venn diagram, where the composite is computed to choose the text colour.
 */

import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

function channels(hex: string): [number, number, number] {
  const value = hex.replace("#", "");
  return [0, 2, 4].map((offset) =>
    Number.parseInt(value.slice(offset, offset + 2), 16)
  ) as [number, number, number];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b]
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`;
}

/** `a` moved toward `b` by `amount` (0 = a, 1 = b). */
export function mixHex(a: string, b: string, amount: number): string {
  const from = channels(a);
  const to = channels(b);
  return toHex(
    [0, 1, 2].map((i) => from[i] + (to[i] - from[i]) * amount) as [
      number,
      number,
      number,
    ]
  );
}

/** A pale version of `color`, for fills behind dark text. */
export function tint(color: string, amount: number): string {
  return mixHex(color, CHART_INK.background, amount);
}

/** The colour of translucent fills laid one over another on white. */
export function compositeOnWhite(
  colors: readonly string[],
  alpha: number
): string {
  let result: string = CHART_INK.background;
  for (const color of colors) {
    result = mixHex(result, color, alpha);
  }
  return result;
}

/**
 * Branch and band colours: the Okabe–Ito palette without yellow (unreadable as a line
 * on white) and black (kept for the ink), in palette order.
 */
export const DIAGRAM_COLORS: readonly string[] = CHART_COLORS.filter(
  (color) => color !== "#F0E442" && color !== "#000000"
);

/** Colour number `index`, starting from palette slot `start` and cycling. */
export function diagramColor(start: number, index: number): string {
  const size = CHART_COLORS.length;
  const slot = CHART_COLORS[((start % size) + size) % size];
  const offset = Math.max(0, DIAGRAM_COLORS.indexOf(slot));
  return DIAGRAM_COLORS[(offset + index) % DIAGRAM_COLORS.length];
}
